import { Position } from "@deck.gl/core"
import { createSlice, Dispatch } from "@reduxjs/toolkit"
import { selectColorFinderFlightById } from "features/entity-caching/flights/selectors"
import { canOpenColorFinderFlight } from "../ColorFinderQueries"
import { setGlobalNotificationContext } from "user/profile/slice"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { clearSelection } from "panels/content/side-panel/slice.new"
import { setInspectorPanelState } from "features/popups/slice"
import { RootState } from "integrations/redux"

export const colorFinderV2SliceName = "color-finder-v2"

export type ColorFinderPanelView = "default" | "flipped" | "fullscreen"
export type ColorFinderFootageQuality = "low" | "medium" | "high"

const getInitialColorFinderState = () => ({
    isOpen: false,
    flightId: "",
    current: {
        commentIndex: -1,
    },
    filters: {
        anomalyOpacity: 100,
        anomalySensitivity: 75,
    },
    activeTool: "" as "" | "point" | "color-sample",
    frameBoundingBox: undefined as [Position, Position, Position, Position] | undefined,
    feedFilters: {
        comments: true,
        points: true,
    },
    footageOptions: {
        source: true,
        anomaly: true,
        anomalySensitivity: 75,
        anomalyOpacity: 62.5,
        resetZoomTrigger: 0,
        isZoomed: false,
    },
    mapSettings: {
        crosshair: true,
        fieldOfView: true,
        flightPath: true,
        synced: true,
    },
    view: "default" as ColorFinderPanelView,
})

const slice = createSlice({
    name: colorFinderV2SliceName,
    initialState: getInitialColorFinderState(),
    reducers: {
        _openColorFinder: (state, action: { payload: { flightId: string } }) => {
            state.isOpen = true
            state.flightId = action.payload.flightId
        },
        closeColorFinder: () => {
            return getInitialColorFinderState()
        },
        setCurrentColorFinderCommentIndex: (state, action: { payload: number }) => {
            state.current.commentIndex = action.payload
        },
        setColorFinderTool: (
            state,
            action: { payload: ReturnType<typeof getInitialColorFinderState>["activeTool"] }
        ) => {
            state.activeTool = action.payload
        },
        setFrameBoundingBox: (
            state,
            action: { payload: ReturnType<typeof getInitialColorFinderState>["frameBoundingBox"] }
        ) => {
            state.frameBoundingBox = action.payload
        },
        setColorFinderFeedFilters: (
            state,
            action: {
                payload: Partial<ReturnType<typeof getInitialColorFinderState>["feedFilters"]>
            }
        ) => {
            state.feedFilters = { ...state.feedFilters, ...action.payload }
        },
        setColorFinderView: (
            state,
            action: { payload: ReturnType<typeof getInitialColorFinderState>["view"] }
        ) => {
            state.view = action.payload
        },
        setColorFinderAnomalySensitivity: (state, action: { payload: number }) => {
            state.footageOptions.anomalySensitivity = action.payload
        },
        setColorFinderAnomalyOpacity: (state, action: { payload: number }) => {
            state.footageOptions.anomalyOpacity = action.payload
        },
        setColorFinderSourceEnabled: (state, action: { payload: boolean }) => {
            state.footageOptions.source = action.payload
        },
        setColorFinderAnomalyEnabled: (state, action: { payload: boolean }) => {
            state.footageOptions.anomaly = action.payload
        },
        setColorFinderMapSettings: (
            state,
            action: {
                payload: Partial<ReturnType<typeof getInitialColorFinderState>["mapSettings"]>
            }
        ) => {
            state.mapSettings = { ...state.mapSettings, ...action.payload }
        },
        resetColorFinderFootageZoom: (state) => {
            state.footageOptions.resetZoomTrigger += 1
        },
        setColorFinderIsZoomed: (state, action: { payload: boolean }) => {
            state.footageOptions.isZoomed = action.payload
        },
        setColorFinderMapIsSynced: (state, action: { payload: boolean }) => {
            state.mapSettings.synced = action.payload
        },
    },
})

export const openColorFinder = (payload: { flightId: string }) => {
    return (dispatch: Dispatch, getState: () => RootState) => {
        const state = getState()
        const flight = selectColorFinderFlightById(payload.flightId)(state)

        const canOpen = canOpenColorFinderFlight(flight)

        if (!canOpen.result) {
            if (canOpen.reason) {
                dispatch(
                    setGlobalNotificationContext({
                        message: canOpen.reason,
                        status: canOpen.status ?? GlobalNotificationStatus.error,
                        timeoutInMs: canOpen.notificationDuration ?? 3000,
                    })
                )
            }
            return
        } else {
            dispatch(clearSelection())
            dispatch(slice.actions._openColorFinder({ flightId: payload.flightId }))
            dispatch(setInspectorPanelState("inactive"))
        }
    }
}

export const {
    closeColorFinder,
    setCurrentColorFinderCommentIndex,
    setColorFinderTool,
    setFrameBoundingBox,
    setColorFinderFeedFilters,
    setColorFinderView,
    setColorFinderAnomalyOpacity,
    setColorFinderAnomalySensitivity,
    setColorFinderMapSettings,
    setColorFinderAnomalyEnabled,
    setColorFinderSourceEnabled,
    setColorFinderIsZoomed,
    resetColorFinderFootageZoom,
    setColorFinderMapIsSynced,
} = slice.actions
export const ColorFinderV2Reducer = { [colorFinderV2SliceName]: slice.reducer }
